import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const alertVariants = cva(
  "relative w-full rounded-lg border p-4 [&>svg~*]:pl-7 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-foreground transition-colors",
  {
    variants: {
      variant: {
        // Primary variants
        primary: "bg-primary/10 border-primary/20 text-primary [&>svg]:text-primary hover:bg-primary/20",
        "primary-light": "bg-primary-light/10 border-primary-light/20 text-primary-light [&>svg]:text-primary-light hover:bg-primary-light/20",
        "primary-dark": "bg-primary-dark/10 border-primary-dark/20 text-primary-dark [&>svg]:text-primary-dark hover:bg-primary-dark/20",
        
        // Secondary variants
        secondary: "bg-secondary/10 border-secondary/20 text-secondary [&>svg]:text-secondary hover:bg-secondary/20",
        "secondary-light": "bg-secondary-light/10 border-secondary-light/20 text-secondary-light [&>svg]:text-secondary-light hover:bg-secondary-light/20",
        "secondary-dark": "bg-secondary-dark/10 border-secondary-dark/20 text-secondary-dark [&>svg]:text-secondary-dark hover:bg-secondary-dark/20",
        
        // Info variants
        info: "bg-info/10 border-info/20 text-info [&>svg]:text-info hover:bg-info/20",
        "info-light": "bg-info-light/10 border-info-light/20 text-info-light [&>svg]:text-info-light hover:bg-info-light/20",
        "info-dark": "bg-info-dark/10 border-info-dark/20 text-info-dark [&>svg]:text-info-dark hover:bg-info-dark/20",
        
        // Success variants
        success: "bg-success/10 border-success/20 text-success [&>svg]:text-success hover:bg-success/20",
        "success-light": "bg-success-light/10 border-success-light/20 text-success-light [&>svg]:text-success-light hover:bg-success-light/20",
        "success-dark": "bg-success-dark/10 border-success-dark/20 text-success-dark [&>svg]:text-success-dark hover:bg-success-dark/20",
        
        // Warning variants
        warning: "bg-warning/10 border-warning/20 text-warning [&>svg]:text-warning hover:bg-warning/20",
        "warning-light": "bg-warning-light/10 border-warning-light/20 text-warning-light [&>svg]:text-warning-light hover:bg-warning-light/20",
        "warning-dark": "bg-warning-dark/10 border-warning-dark/20 text-warning-dark [&>svg]:text-warning-dark hover:bg-warning-dark/20",
        
        // Error variants
        error: "bg-error/10 border-error/20 text-error [&>svg]:text-error hover:bg-error/20",
        "error-light": "bg-error-light/10 border-error-light/20 text-error-light [&>svg]:text-error-light hover:bg-error-light/20",
        "error-dark": "bg-error-dark/10 border-error-dark/20 text-error-dark [&>svg]:text-error-dark hover:bg-error-dark/20",
        
        // Default/Neutral variants
        default: "bg-background border-border text-foreground hover:bg-action/10",
        muted: "bg-muted/50 border-muted/20 text-muted-foreground hover:bg-muted",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

const Alert = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant, ...props }, ref) => (
  <div
    ref={ref}
    role="alert"
    className={cn(alertVariants({ variant }), className)}
    {...props}
  />
))
Alert.displayName = "Alert"

const AlertTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn("mb-1 font-medium leading-none tracking-tight", className)}
    {...props}
  />
))
AlertTitle.displayName = "AlertTitle"

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-sm [&_p]:leading-relaxed", className)}
    {...props}
  />
))
AlertDescription.displayName = "AlertDescription"

export { Alert, AlertTitle, AlertDescription }
